/**
 * Translation helpers for user-visible strings stored in the database in Portuguese.
 * Translations are keyed by a normalized slug of the original PT value.
 * Falls back to the original value when no translation is found.
 */

export function normalizeKey(value: string): string {
  return (value || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

const SPORT_MAP: Record<string, { en: string; es: string }> = {
  futebol: { en: "Soccer", es: "Fútbol" },
  futsal: { en: "Futsal", es: "Futsal" },
  volei: { en: "Volleyball", es: "Vóley" },
  voleibol: { en: "Volleyball", es: "Vóley" },
  futevolei: { en: "Footvolley", es: "Futvóley" },
  futevolei_de_areia: { en: "Footvolley", es: "Futvóley" },
  basquete: { en: "Basketball", es: "Baloncesto" },
  basquetebol: { en: "Basketball", es: "Baloncesto" },
  handebol: { en: "Handball", es: "Balonmano" },
  tenis: { en: "Tennis", es: "Tenis" },
  tenis_de_mesa: { en: "Table Tennis", es: "Tenis de Mesa" },
  tenis_de_praia: { en: "Beach Tennis", es: "Beach Tennis" },
  beach_tennis: { en: "Beach Tennis", es: "Beach Tennis" },
  beachtennis: { en: "Beach Tennis", es: "Beach Tennis" },
  padel: { en: "Padel", es: "Pádel" },
  squash: { en: "Squash", es: "Squash" },
  badminton: { en: "Badminton", es: "Bádminton" },
  futebol_de_areia: { en: "Beach Soccer", es: "Fútbol Playa" },
  beach_soccer: { en: "Beach Soccer", es: "Fútbol Playa" },
};

const MODALITY_MAP: Record<string, { en: string; es: string }> = {
  campo: { en: "Field", es: "Campo" },
  society: { en: "Society", es: "Society" },
  futsal: { en: "Futsal", es: "Futsal" },
  praia: { en: "Beach", es: "Playa" },
  areia: { en: "Beach Soccer", es: "Fútbol Playa" },
  beach_soccer: { en: "Beach Soccer", es: "Fútbol Playa" },
  quadra: { en: "Indoor", es: "Sala" },
  sentado: { en: "Sitting", es: "Sentado" },
  "5x5": { en: "5x5", es: "5x5" },
  "3x3": { en: "3x3", es: "3x3" },
  "2x2": { en: "2x2", es: "2x2" },
  tradicional: { en: "Traditional", es: "Tradicional" },
  simples: { en: "Singles", es: "Individual" },
  duplas: { en: "Doubles", es: "Dobles" },
  individual: { en: "Singles", es: "Individual" },
  masculino: { en: "Men's", es: "Masculino" },
  feminino: { en: "Women's", es: "Femenino" },
  misto: { en: "Mixed", es: "Mixto" },
};

// Player position categories (full names) used across all sports.
const POSITION_MAP: Record<string, { en: string; es: string }> = {
  // Football / Futsal / Handball / Beach
  goleiro: { en: "Goalkeeper", es: "Portero" },
  zagueiro: { en: "Centre-back", es: "Defensa central" },
  lateral: { en: "Full-back", es: "Lateral" },
  lateral_direito: { en: "Right-back", es: "Lateral derecho" },
  lateral_esquerdo: { en: "Left-back", es: "Lateral izquierdo" },
  volante: { en: "Defensive midfielder", es: "Mediocampista defensivo" },
  meia: { en: "Midfielder", es: "Mediocampista" },
  meia_atacante: { en: "Attacking midfielder", es: "Mediapunta" },
  meio_campo: { en: "Midfielder", es: "Mediocampista" },
  atacante: { en: "Forward", es: "Delantero" },
  ponta: { en: "Winger", es: "Extremo" },
  ponta_direita: { en: "Right winger", es: "Extremo derecho" },
  ponta_esquerda: { en: "Left winger", es: "Extremo izquierdo" },
  ponta_direito: { en: "Right winger", es: "Extremo derecho" },
  ponta_esquerdo: { en: "Left winger", es: "Extremo izquierdo" },
  fixo: { en: "Defender", es: "Fijo" },
  ala: { en: "Wing", es: "Ala" },
  ala_direito: { en: "Right wing", es: "Ala derecho" },
  ala_esquerdo: { en: "Left wing", es: "Ala izquierdo" },
  pivo: { en: "Pivot", es: "Pívot" },
  central: { en: "Centre", es: "Central" },
  // Basketball
  armador: { en: "Point guard", es: "Base" },
  ala_armador: { en: "Shooting guard", es: "Escolta" },
  ala_pivo: { en: "Power forward", es: "Ala-pívot" },
  // Volleyball
  levantador: { en: "Setter", es: "Colocador" },
  oposto: { en: "Opposite", es: "Opuesto" },
  ponteiro: { en: "Outside hitter", es: "Punta" },
  libero: { en: "Libero", es: "Líbero" },
  // Generic
  jogador: { en: "Player", es: "Jugador" },
};

// Numbered variants ("Jogador 1", "Zagueiro 2", "Meio-Campo 1") collapse to base + number.
function translatePositionWithIndex(value: string, target: "en" | "es"): string | null {
  const m = value.match(/^(.*?)[\s-]+(\d+)$/);
  if (!m) return null;
  const base = m[1].trim();
  const idx = m[2];
  const baseKey = normalizeKey(base);
  const entry = POSITION_MAP[baseKey];
  if (!entry) return null;
  return `${entry[target]} ${idx}`;
}

export function translatePosition(value: string | null | undefined, lang: string): string {
  if (!value) return "";
  const target = pickLang(lang);
  if (target === "pt") return value;
  const indexed = translatePositionWithIndex(value, target);
  if (indexed) return indexed;
  const key = normalizeKey(value);
  const entry = POSITION_MAP[key];
  return entry ? entry[target] : value;
}

// Short labels shown on the pitch ("GOL", "ZAG", "ARM"...). We keep them
// language-aware so the canvas overlay matches the rest of the UI.
const POSITION_SHORT_MAP: Record<string, { en: string; es: string }> = {
  GOL: { en: "GK", es: "POR" },
  ZAG: { en: "CB", es: "DFC" },
  LD: { en: "RB", es: "LD" },
  LE: { en: "LB", es: "LI" },
  VOL: { en: "DM", es: "MCD" },
  MEI: { en: "MF", es: "MC" },
  ATA: { en: "FW", es: "DEL" },
  PD: { en: "RW", es: "ED" },
  PE: { en: "LW", es: "EI" },
  FIX: { en: "DEF", es: "FIJ" },
  ALA: { en: "W", es: "AL" },
  PIV: { en: "PIV", es: "PIV" },
  CEN: { en: "C", es: "C" },
  ARM: { en: "PG", es: "BAS" },
  AA: { en: "SG", es: "ESC" },
  AP: { en: "PF", es: "AP" },
  LEV: { en: "S", es: "COL" },
  OPO: { en: "OPP", es: "OPU" },
  PON: { en: "OH", es: "PUN" },
  LIB: { en: "L", es: "LIB" },
  MD: { en: "RM", es: "MD" },
  ME: { en: "LM", es: "MI" },
  J1: { en: "P1", es: "J1" },
  J2: { en: "P2", es: "J2" },
  J3: { en: "P3", es: "J3" },
  J4: { en: "P4", es: "J4" },
  J5: { en: "P5", es: "J5" },
  J6: { en: "P6", es: "J6" },
};

export function translatePositionShort(value: string | null | undefined, lang: string): string {
  if (!value) return "";
  const target = pickLang(lang);
  if (target === "pt") return value;
  const entry = POSITION_SHORT_MAP[value];
  return entry ? entry[target] : value;
}

const CHAMPIONSHIP_TYPE_MAP: Record<string, { en: string; es: string }> = {
  grupo_mata_mata: { en: "Groups + Knockout", es: "Grupos + Eliminatorias" },
  mata_mata: { en: "Knockout", es: "Eliminatorias" },
  pontos_corridos: { en: "League", es: "Liga" },
  pontos_corridos_mata_mata: { en: "League + Knockout", es: "Liga + Eliminatorias" },
  fase_de_grupos: { en: "Group stage", es: "Fase de grupos" },
  oitavas_de_final: { en: "Round of 16", es: "Octavos de final" },
  quartas_de_final: { en: "Quarterfinals", es: "Cuartos de final" },
  semifinal: { en: "Semifinal", es: "Semifinal" },
  semifinais: { en: "Semifinals", es: "Semifinales" },
  final: { en: "Final", es: "Final" },
  terceiro_lugar: { en: "Third place", es: "Tercer lugar" },
  disputa_3_lugar: { en: "Third place match", es: "Partido por el tercer lugar" },
};

function pickLang(lang: string): "pt" | "en" | "es" {
  const base = (lang || "pt").split("-")[0].toLowerCase();
  if (base === "en") return "en";
  if (base === "es") return "es";
  return "pt";
}

/**
 * Picks the localized field from a row that has `<base>`, `<base>_en`, `<base>_es` columns.
 * Falls back to the PT base value when the localized one is empty.
 */
export function pickLocalizedField<T extends Record<string, any>>(
  row: T | null | undefined,
  baseField: string,
  lang: string,
): string {
  if (!row) return "";
  const target = pickLang(lang);
  if (target === "pt") return (row[baseField] ?? "") as string;
  const localized = row[`${baseField}_${target}`];
  if (typeof localized === "string" && localized.trim().length > 0) return localized;
  return (row[baseField] ?? "") as string;
}

function translateFromMap(
  value: string | null | undefined,
  map: Record<string, { en: string; es: string }>,
  lang: string,
): string {
  if (!value) return "";
  const target = pickLang(lang);
  if (target === "pt") return value;
  const key = normalizeKey(value);
  const entry = map[key];
  return entry ? entry[target] : value;
}

export function translateSportName(value: string | null | undefined, lang: string): string {
  return translateFromMap(value, SPORT_MAP, lang);
}

export function translateModalityName(value: string | null | undefined, lang: string): string {
  return translateFromMap(value, MODALITY_MAP, lang);
}

export function translateChampionshipTypeName(value: string | null | undefined, lang: string): string {
  return translateFromMap(value, CHAMPIONSHIP_TYPE_MAP, lang);
}

/**
 * Joins sport + modality into a localized label.
 * Handles special combinations that should collapse into a single term in non-PT
 * languages (e.g. "Futebol – Areia" → "Beach Soccer" / "Fútbol Playa").
 */
export function translateSportModalityCombo(
  sport: string | null | undefined,
  modality: string | null | undefined,
  lang: string,
): string {
  const target = pickLang(lang);
  const sportKey = normalizeKey(sport || "");
  const modalityKey = normalizeKey(modality || "");

  // Collapse "Futebol + Areia" into a single localized "Beach Soccer" label
  if (target !== "pt" && sportKey === "futebol" && modalityKey === "areia") {
    return target === "en" ? "Beach Soccer" : "Fútbol Playa";
  }

  const parts = [translateSportName(sport, lang), translateModalityName(modality, lang)].filter(Boolean);
  return parts.join(" – ");
}

